import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient, type PaginatedResult } from './api-client';

/**
 * Admin catalogue endpoints (spec §14, §15, §32).
 *
 * The types mirror the API's admin mappers, which are deliberately a different
 * shape from the storefront's — staff see drafts, soft-delete markers and stock,
 * and the public catalogue never does.
 */

export type ProductStatus = 'DRAFT' | 'ACTIVE' | 'INACTIVE' | 'ARCHIVED';
export type ProductMediaType = 'MAIN' | 'GALLERY' | 'HOVER' | 'THUMBNAIL' | 'VIDEO';

export interface PriceTier {
  id?: string;
  minQuantity: number;
  maxQuantity: number | null;
  pricePerUnit: number;
}

export interface AdminProductListItem {
  id: string;
  name: string;
  slug: string;
  sku: string;
  productCode: string | null;
  status: ProductStatus;
  category: { id: string; name: string; slug: string } | null;
  brand: { id: string; name: string; slug: string } | null;
  material: string | null;
  color: string | null;
  moq: number;
  quantityIncrement: number;
  basePrice: number;
  wholesalePrice: number;
  gstPercent: number;
  discountPercent: number;
  tierCount: number;
  lowestTierPrice: number | null;
  variantCount: number;
  imageCount: number;
  onHandQuantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  isLowStock: boolean;
  isUntracked: boolean;
  isFeatured: boolean;
  isNewArrival: boolean;
  isBestSeller: boolean;
  soldCount: number;
  primaryImageUrl: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProductImage {
  id: string;
  type: ProductMediaType;
  url: string;
  thumbnailUrl: string | null;
  altText: string | null;
  displayOrder: number;
}

export interface ProductVariant {
  id: string;
  sku: string;
  name: string;
  color: string | null;
  size: string | null;
  material: string | null;
  priceAdjustment: number;
  moq: number | null;
  imageUrl: string | null;
  isActive: boolean;
  displayOrder: number;
  availableQuantity: number;
}

/**
 * A product's search and social metadata, as the form edits it.
 *
 * Deliberately not the shape the storefront receives: `metaKeywords` stays the
 * raw comma-separated string a merchandiser types, and `focusKeyword` is
 * editorial — it steers the SERP preview's checks and is never rendered on a
 * page. Every field is an override; blank means "derive it from the product".
 */
export interface AdminProductSeo {
  metaTitle: string | null;
  metaDescription: string | null;
  metaKeywords: string | null;
  focusKeyword: string | null;
  canonicalUrl: string | null;
  ogTitle: string | null;
  ogDescription: string | null;
  ogImageUrl: string | null;
  noIndex: boolean;
  noFollow: boolean;
}

export interface AdminProductDetail extends AdminProductListItem {
  shortDescription: string | null;
  description: string | null;
  subcategory: { id: string; name: string; slug: string } | null;
  size: string | null;
  weightGrams: number | null;
  dimensions: string | null;
  capacityLitres: number | null;
  maxOrderQuantity: number | null;
  hsnCode: string | null;
  tags: string[];
  seo: AdminProductSeo;
  ratingAverage: number;
  ratingCount: number;
  viewCount: number;
  priceTiers: Array<PriceTier & { id: string }>;
  specifications: Array<{ id: string; name: string; value: string; displayOrder: number }>;
  variants: ProductVariant[];
  images: ProductImage[];
  stockByWarehouse: Array<{
    warehouseId: string;
    warehouseName: string;
    warehouseCode: string;
    variantId: string | null;
    quantity: number;
    reservedQuantity: number;
    availableQuantity: number;
    lowStockThreshold: number;
  }>;
}

export interface AdminCategory {
  id: string;
  name: string;
  slug: string;
  parentId: string | null;
  parentName: string | null;
  description: string | null;
  imageUrl: string | null;
  iconUrl: string | null;
  bannerUrl: string | null;
  displayOrder: number;
  isActive: boolean;
  isFeatured: boolean;
  metaTitle: string | null;
  metaDescription: string | null;
  productCount: number;
  childCount: number;
  createdAt: string;
}

export interface AdminBrand {
  id: string;
  name: string;
  slug: string;
  logoUrl: string | null;
  description: string | null;
  website: string | null;
  isActive: boolean;
  isFeatured: boolean;
  displayOrder: number;
  metaTitle: string | null;
  metaDescription: string | null;
  productCount: number;
  createdAt: string;
}

export interface ProductListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: ProductStatus[];
  categoryId?: string;
  brandId?: string;
  lowStock?: boolean;
  untracked?: boolean;
  includeDeleted?: boolean;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface ImportSummary {
  dryRun: boolean;
  totalRows: number;
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{
    rowNumber: number;
    sku: string | null;
    errors: Array<{ field: string; message: string; code?: string }>;
  }>;
}

const keys = {
  all: ['admin', 'catalog'] as const,
  products: () => [...keys.all, 'products'] as const,
  productList: (params: ProductListParams) => [...keys.products(), 'list', params] as const,
  product: (id: string) => [...keys.products(), 'detail', id] as const,
  categories: () => [...keys.all, 'categories'] as const,
  brands: () => [...keys.all, 'brands'] as const,
};

/** Every filter the list screen can apply, as query parameters. */
function toQuery(params: ProductListParams): Record<string, string | number | boolean | undefined> {
  return {
    page: params.page,
    limit: params.limit,
    search: params.search || undefined,
    status: params.status?.length ? params.status.join(',') : undefined,
    categoryId: params.categoryId || undefined,
    brandId: params.brandId || undefined,
    // Only sent when true: `lowStock=false` is not a filter, it is "no filter".
    lowStock: params.lowStock || undefined,
    untracked: params.untracked || undefined,
    includeDeleted: params.includeDeleted || undefined,
    sortBy: params.sortBy,
    sortOrder: params.sortOrder,
  };
}

// --- Products ----------------------------------------------------------------

export function useProducts(params: ProductListParams) {
  return useQuery({
    queryKey: keys.productList(params),
    queryFn: () =>
      apiClient.get<PaginatedResult<AdminProductListItem>>('/admin/catalog/products', {
        query: toQuery(params),
      }),
    placeholderData: (previous) => previous,
  });
}

export function useProduct(productId: string | null) {
  return useQuery({
    queryKey: keys.product(productId ?? ''),
    queryFn: () => apiClient.get<AdminProductDetail>(`/admin/catalog/products/${productId}`),
    enabled: productId !== null,
  });
}

export function useSaveProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      body,
    }: {
      productId: string | null;
      body: Record<string, unknown>;
    }) =>
      productId
        ? apiClient.patch<AdminProductDetail>(`/admin/catalog/products/${productId}`, body)
        : apiClient.post<AdminProductDetail>('/admin/catalog/products', body),
    onSuccess: (product) => {
      queryClient.setQueryData(keys.product(product.id), product);
      void queryClient.invalidateQueries({ queryKey: keys.products() });
    },
  });
}

export function useDeleteProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productId: string) =>
      apiClient.delete<void>(`/admin/catalog/products/${productId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.products() }),
  });
}

export function useRestoreProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (productId: string) =>
      apiClient.post<AdminProductDetail>(`/admin/catalog/products/${productId}/restore`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.products() }),
  });
}

// --- Images ------------------------------------------------------------------

export function useUploadProductImage(productId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      type,
      altText,
    }: {
      file: File;
      type: ProductMediaType;
      altText: string;
    }) => {
      const form = new FormData();
      form.append('file', file);
      form.append('type', type);
      if (altText) form.append('altText', altText);

      return apiClient.upload<ProductImage>(`/admin/catalog/products/${productId}/images`, form);
    },
    onSuccess: () => {
      if (productId) void queryClient.invalidateQueries({ queryKey: keys.product(productId) });
      void queryClient.invalidateQueries({ queryKey: keys.products() });
    },
  });
}

export function useDeleteProductImage(productId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (imageId: string) => apiClient.delete<void>(`/admin/catalog/images/${imageId}`),
    onSuccess: () => {
      if (productId) void queryClient.invalidateQueries({ queryKey: keys.product(productId) });
      void queryClient.invalidateQueries({ queryKey: keys.products() });
    },
  });
}

export function useUpdateProductImage(productId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      imageId,
      ...body
    }: {
      imageId: string;
      type?: ProductMediaType;
      altText?: string | null;
    }) => apiClient.patch<ProductImage>(`/admin/catalog/images/${imageId}`, body),
    onSuccess: () => {
      if (productId) void queryClient.invalidateQueries({ queryKey: keys.product(productId) });
      void queryClient.invalidateQueries({ queryKey: keys.products() });
    },
  });
}

/**
 * Apply a new gallery order.
 *
 * The whole order is sent, not a moved pair: the server writes it in one
 * transaction, so the gallery can never render half-reordered.
 */
export function useReorderProductImages(productId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (images: Array<{ id: string; displayOrder: number }>) =>
      apiClient.patch<ProductImage[]>(`/admin/catalog/products/${productId}/images/reorder`, {
        images,
      }),
    onSuccess: () => {
      if (productId) void queryClient.invalidateQueries({ queryKey: keys.product(productId) });
      void queryClient.invalidateQueries({ queryKey: keys.products() });
    },
  });
}

// --- Variants ----------------------------------------------------------------

export interface VariantInput {
  sku: string;
  name: string;
  color?: string | null;
  size?: string | null;
  material?: string | null;
  priceAdjustment?: number;
  moq?: number | null;
  isActive?: boolean;
  displayOrder?: number;
}

export function useSaveVariant(productId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ variantId, body }: { variantId: string | null; body: VariantInput }) =>
      variantId
        ? apiClient.patch<ProductVariant>(`/admin/catalog/variants/${variantId}`, body)
        : apiClient.post<ProductVariant>(`/admin/catalog/products/${productId}/variants`, body),
    onSuccess: () => {
      if (productId) void queryClient.invalidateQueries({ queryKey: keys.product(productId) });
      void queryClient.invalidateQueries({ queryKey: keys.products() });
    },
  });
}

export function useDeleteVariant(productId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variantId: string) =>
      apiClient.delete<void>(`/admin/catalog/variants/${variantId}`),
    onSuccess: () => {
      if (productId) void queryClient.invalidateQueries({ queryKey: keys.product(productId) });
      void queryClient.invalidateQueries({ queryKey: keys.products() });
    },
  });
}

// --- Import ------------------------------------------------------------------

export function useImportProducts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      file,
      dryRun,
      updateExisting,
    }: {
      file: File;
      dryRun: boolean;
      updateExisting: boolean;
    }) => {
      const form = new FormData();
      form.append('file', file);
      form.append('dryRun', String(dryRun));
      form.append('updateExisting', String(updateExisting));

      return apiClient.upload<ImportSummary>('/admin/catalog/products/import', form);
    },
    onSuccess: (summary) => {
      // A dry run changed nothing, so there is nothing to refetch.
      if (!summary.dryRun) {
        void queryClient.invalidateQueries({ queryKey: keys.products() });
      }
    },
  });
}

// --- Categories --------------------------------------------------------------

export function useCategories() {
  return useQuery({
    queryKey: keys.categories(),
    queryFn: () => apiClient.get<AdminCategory[]>('/admin/catalog/categories'),
  });
}

export function useSaveCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string | null; body: Record<string, unknown> }) =>
      id
        ? apiClient.patch<AdminCategory>(`/admin/catalog/categories/${id}`, body)
        : apiClient.post<AdminCategory>('/admin/catalog/categories', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.categories() }),
  });
}

export function useDeleteCategory() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/admin/catalog/categories/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.categories() }),
  });
}

// --- Brands ------------------------------------------------------------------

export function useBrands() {
  return useQuery({
    queryKey: keys.brands(),
    queryFn: () => apiClient.get<AdminBrand[]>('/admin/catalog/brands'),
  });
}

export function useSaveBrand() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string | null; body: Record<string, unknown> }) =>
      id
        ? apiClient.patch<AdminBrand>(`/admin/catalog/brands/${id}`, body)
        : apiClient.post<AdminBrand>('/admin/catalog/brands', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.brands() }),
  });
}

export function useDeleteBrand() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/admin/catalog/brands/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: keys.brands() }),
  });
}

/**
 * Order categories so a child always follows its parent, with a depth for
 * indentation. The API returns a flat list precisely so the client can decide
 * how to present the hierarchy.
 */
export function toCategoryTree(
  categories: AdminCategory[],
): Array<AdminCategory & { depth: number }> {
  const byParent = new Map<string | null, AdminCategory[]>();

  for (const category of categories) {
    const siblings = byParent.get(category.parentId) ?? [];
    siblings.push(category);
    byParent.set(category.parentId, siblings);
  }

  const ordered: Array<AdminCategory & { depth: number }> = [];

  const walk = (parentId: string | null, depth: number): void => {
    for (const category of byParent.get(parentId) ?? []) {
      ordered.push({ ...category, depth });
      walk(category.id, depth + 1);
    }
  };

  walk(null, 0);

  // A category whose parent is inactive or filtered out would otherwise vanish
  // from the table entirely.
  const seen = new Set(ordered.map((category) => category.id));
  for (const category of categories) {
    if (!seen.has(category.id)) {
      ordered.push({ ...category, depth: 0 });
    }
  }

  return ordered;
}
